import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Bell, Search, Sun, Moon, User, LogOut, ChevronDown, Crown, Award, Star, Shield, UserX, AlertTriangle, Clock, DollarSign, FileText, Users, MessageSquare, CheckCircle2, Target, Package, BookOpen, Building2, Briefcase, History, X, TrendingUp, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { opportunities, endUsersWithContacts, priceList } from './data/mockData';
import { useAuth } from '../Services/AuthContext';
import { getTierColor, getTierIcon, getTierDisplayName } from './ui/tierUtil';
import { apiFetch } from "../utils/api";
import { capitalizeFirstLetter, toUpper } from '../utils/formatUtils';


interface HeaderProps {
  isDarkMode: boolean;
  setIsDarkMode: (isDarkMode: boolean) => void;
  setActiveSection: (section: string) => void;
  navigateToAnnouncement?: (announcementId: number) => void;
}

export function Header({ isDarkMode, setIsDarkMode, setActiveSection, navigateToAnnouncement }: HeaderProps) {

  const { login, user, token, idToken, currentPartner, setCurrentPartner, appUser, logout } = useAuth();
  
  // Universal Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [notifications, setNotifications] = useState([]);

  const fetchAnnouncements = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, `/api/announcements`);
    res.then(async (response) => {
      setNotifications(response.data);
    });
  };

  const markAnnouncementRead = async (notification) => {
    const res = await apiFetch(await idToken(), "/api/announcements/mark_read", {
      method: "PATCH",
      body: JSON.stringify({id: notification})
    });
  };

  // Load dark mode preference from cookie on mount
  useEffect(() => {
    const darkModeCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('darkMode='));
    if (darkModeCookie) {
      const value = darkModeCookie.split('=')[1];
      setIsDarkMode(value === 'true');
    }
  }, []);

  // Save dark mode preference to cookie whenever it changes
  useEffect(() => {
    document.cookie = `darkMode=${isDarkMode}; path=/; max-age=31536000`; // 1 year expiry
  }, [isDarkMode]);

  // Load recent searches from localStorage
  useEffect(() => {
    fetchAnnouncements();
    const saved = localStorage.getItem('worldsview-recent-searches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load recent searches', e);
      }
    }
  }, []);

  const switchPartner = (partner) => {
    setCurrentPartner(partner);
    // Save selected partner ID to cookie
    document.cookie = `selectedPartnerId=${partner.id}; path=/; max-age=31536000`; // 1 year expiry
  }

  // Restore selected partner from cookie on mount
  useEffect(() => {
    if (!appUser?.partners || appUser.partners.length === 0) return;

    const partnerCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('selectedPartnerId='));
    
    if (partnerCookie) {
      const savedPartnerId = parseInt(partnerCookie.split('=')[1]);
      const savedPartner = appUser.partners.find(p => p.id === savedPartnerId);
      
      // If saved partner exists, use it; otherwise default to first partner
      if (savedPartner) {
        setCurrentPartner(savedPartner);
      } else if (!currentPartner?.id) {
        // Only set default if no partner is currently set
        setCurrentPartner(appUser.partners[0]);
      }
    } else if (!currentPartner?.id) {
      // No cookie found, default to first partner if none is set
      setCurrentPartner(appUser.partners[0]);
    }
  }, [appUser?.partners]);

  // Save recent searches to localStorage
  const saveRecentSearch = (query: string) => {
    if (!query.trim() || query.length < 2) return;
    
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('worldsview-recent-searches', JSON.stringify(updated));
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('worldsview-recent-searches');
  };

  const mockContent = [
    { id: '1', title: 'AutoCAD 2024 Getting Started Guide', type: 'PDF', category: 'Training Materials' },
    { id: '2', title: 'Revit Architecture Best Practices', type: 'Video', category: 'Training Materials' },
    { id: '3', title: 'Sales Presentation Template', type: 'PowerPoint', category: 'Sales Resources' },
    { id: '4', title: 'Product Comparison Guide', type: 'PDF', category: 'Sales Resources' },
    { id: '5', title: 'Partner Marketing Kit', type: 'ZIP', category: 'Marketing Materials' },
  ];

  // Universal search function
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const query = searchQuery.toLowerCase();

    // Simulate search delay for better UX
    const searchTimeout = setTimeout(() => {
      const results: any[] = [];

      // Search content library
      mockContent
        .filter(item => 
          item.title.toLowerCase().includes(query) || 
          item.category.toLowerCase().includes(query) ||
          item.type.toLowerCase().includes(query)
        )
        .forEach(item => {
          results.push({
            type: 'content',
            icon: BookOpen,
            color: 'text-worldsview-green',
            title: item.title,
            subtitle: item.category,
            meta: item.type,
            action: () => {
              setActiveSection('content-library');
              saveRecentSearch(searchQuery);
              setShowSearchResults(false);
              setSearchQuery('');
            }
          });
        });

      // Search opportunities
      opportunities
        .filter(item => 
          item.name.toLowerCase().includes(query) || 
          item.customer.toLowerCase().includes(query) ||
          item.product.toLowerCase().includes(query) ||
          item.id.toLowerCase().includes(query)
        )
        .forEach(item => {
          results.push({
            type: 'opportunity',
            icon: Target,
            color: 'text-worldsview-purple',
            title: item.name,
            subtitle: item.customer,
            meta: `${item.value} • ${item.stage}`,
            action: () => {
              setActiveSection('sales-renewals');
              saveRecentSearch(searchQuery);
              setShowSearchResults(false);
              setSearchQuery('');
            }
          });
        });

      // Search customers/end users
      endUsersWithContacts
        .filter(item => 
          item.name.toLowerCase().includes(query) || 
          item.contactPerson.toLowerCase().includes(query) ||
          item.industry.toLowerCase().includes(query) ||
          item.csn.toLowerCase().includes(query)
        )
        .forEach(item => {
          results.push({
            type: 'customer',
            icon: Building2,
            color: 'text-worldsview-orange',
            title: item.name,
            subtitle: item.contactPerson,
            meta: `${item.industry} • ${item.status}`,
            action: () => {
              setActiveSection('sales-renewals');
              saveRecentSearch(searchQuery);
              setShowSearchResults(false);
              setSearchQuery('');
            }
          });
        });

      // Search products
      priceList
        .filter(item => 
          item.description.toLowerCase().includes(query) || 
          item.sku.toLowerCase().includes(query) ||
          item.itemGroup.toLowerCase().includes(query)
        )
        .slice(0, 5) // Limit product results
        .forEach(item => {
          results.push({
            type: 'product',
            icon: Package,
            color: 'text-worldsview-blue',
            title: item.description,
            subtitle: item.itemGroup,
            meta: `R${item.unitPrice.toLocaleString()}`,
            action: () => {
              setActiveSection('price-lookup');
              saveRecentSearch(searchQuery);
              setShowSearchResults(false);
              setSearchQuery('');
            }
          });
        });

      setSearchResults(results);
      setShowSearchResults(true);
      setIsSearching(false);
      setSelectedResultIndex(-1);
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  // Click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSearchResults) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedResultIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedResultIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
          searchResults[selectedResultIndex].action();
        }
        break;
      case 'Escape':
        setShowSearchResults(false);
        setSearchQuery('');
        inputRef.current?.blur();
        break;
    }
  };

  // Group results by type
  const groupedResults = searchResults.reduce((acc: any, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  // Highlight search term in text
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={i} className="bg-worldsview-blue/20 font-medium">{part}</span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
    );
  };
  
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'pricelist': return <DollarSign className="w-4 h-4 text-worldsview-green" />;
      case 'content': return <FileText className="w-4 h-4 text-worldsview-blue" />;
      case 'announcement': return <MessageSquare className="w-4 h-4 text-worldsview-purple" />;
      default: return <Bell className="w-4 h-4 text-muted-foreground" />;
    }
  };
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-worldsview-red';
      case 'medium': return 'bg-worldsview-orange';
      case 'low': return 'bg-worldsview-green';
      default: return 'bg-gray-500';
    }
  };
  
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now.getTime() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };
  
  const markAsRead = async (id: string) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    markAnnouncementRead(id);
  };
  
  const markAllAsRead = async () => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
    notifications.forEach(n => {
      markAnnouncementRead(n.id);
    });
  };
  
  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  
  const handleNotificationClick = (notification: any) => {
    
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (navigateToAnnouncement) {
      navigateToAnnouncement(notification.id);
    } else {
      setActiveSection('Announcements');
    }
  };
    
  if (!user) return null;

  const TierIcon = getTierIcon(currentPartner?.tier);
  const tierColor = getTierColor(currentPartner?.tier);

  return (
    <header className="bg-card border-b border-border px-6 py-4 sticky top-0 z-50 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 flex items-center">
              <img 
                src={isDarkMode ? "/LogoDark.png" : "/Logo.png"} 
                alt="Worldsview" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1>Partner Portal</h1>
              <p className="text-sm text-muted-foreground">{currentPartner?.name || 'Worldsview Portal'}</p>
            </div>
          </div>
          
        </div>

        <div className="flex items-center space-x-4">
          {/* Universal Search */}

          {/* <div className="relative" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none z-10" />
            <Input
              ref={inputRef}
              placeholder="Universal search..."
              className="pl-10 pr-10 w-64 glass-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (searchQuery.length >= 2) {
                  setShowSearchResults(true);
                }
              }}
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 hover:bg-transparent"
                onClick={() => {
                  setSearchQuery('');
                  setShowSearchResults(false);
                  inputRef.current?.focus();
                }}
              >
                <X className="w-3 h-3" />
              </Button>
            )}

            
            {showSearchResults && (
              <div className="absolute top-full mt-2 w-[500px] max-h-[600px] overflow-hidden bg-card border border-border shadow-lg rounded-lg z-50 animate-scale-in">
                <div className="max-h-[600px] overflow-y-auto">
                  
                  {searchQuery.length < 2 && recentSearches.length > 0 && (
                    <div className="p-3 border-b border-border/50">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Recent Searches
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearRecentSearches}
                          className="text-xs h-6 px-2 text-muted-foreground hover:text-foreground"
                        >
                          Clear
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {recentSearches.map((search, index) => (
                          <button
                            key={index}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-left"
                            onClick={() => {
                              setSearchQuery(search);
                              inputRef.current?.focus();
                            }}
                          >
                            <History className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm flex-1 truncate">{search}</span>
                            <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  
                  {isSearching && (
                    <div className="p-8 text-center">
                      <div className="loading-skeleton w-8 h-8 rounded-full mx-auto mb-3"></div>
                      <p className="text-sm text-muted-foreground">Searching...</p>
                    </div>
                  )}

                  
                  {!isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
                    <div className="p-8 text-center">
                      <Search className="w-8 h-8 mx-auto mb-3 text-muted-foreground opacity-50" />
                      <p className="text-sm font-medium mb-1">No results found</p>
                      <p className="text-xs text-muted-foreground">
                        Try searching for announcements, content, opportunities, customers, or products
                      </p>
                    </div>
                  )}

                  
                  {!isSearching && searchResults.length > 0 && (
                    <div className="py-2">
                      
                      <div className="px-4 py-2 border-b border-border/50">
                        <p className="text-xs text-muted-foreground">
                          Found <span className="font-medium text-foreground">{searchResults.length}</span> results
                        </p>
                      </div>

                      
                      {Object.keys(groupedResults).map((type) => (
                        <div key={type} className="border-b border-border/50 last:border-0">
                          <div className="px-4 py-2 bg-muted/30">
                            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {getTypeLabel(type)}
                            </h4>
                          </div>
                          <div className="divide-y divide-border/30">
                            {groupedResults[type].map((result: any, index: number) => {
                              const globalIndex = searchResults.indexOf(result);
                              const Icon = result.icon;
                              const isSelected = globalIndex === selectedResultIndex;

                              return (
                                <button
                                  key={globalIndex}
                                  className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left ${
                                    isSelected ? 'bg-accent/70' : ''
                                  }`}
                                  onClick={result.action}
                                  onMouseEnter={() => setSelectedResultIndex(globalIndex)}
                                >
                                  <div className={`flex-shrink-0 mt-0.5 ${result.color}`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">
                                      {highlightText(result.title, searchQuery)}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                                      {result.subtitle}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0 text-xs text-muted-foreground">
                                    {result.meta}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      
                      <div className="px-4 py-2 bg-muted/20 border-t border-border/50">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-xs">↑↓</kbd>
                              Navigate
                            </span>
                            <span className="flex items-center gap-1">
                              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-xs">↵</kbd>
                              Select
                            </span>
                            <span className="flex items-center gap-1">
                              <kbd className="px-1.5 py-0.5 bg-background border border-border rounded text-xs">Esc</kbd>
                              Close
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div> */}

          <Badge 
            className="text-white flex items-center gap-2 px-3 py-1 text-sm font-medium backdrop-blur-md"
            style={{ backgroundColor: tierColor }}
          >
            <TierIcon className="w-4 h-4" />
            {capitalizeFirstLetter(currentPartner?.tier)}
          </Badge>

          <div className="flex items-center space-x-2">
            <Sun className="w-4 h-4" />
            <Switch
              checked={isDarkMode}
              onCheckedChange={setIsDarkMode}
            />
            <Moon className="w-4 h-4" />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="relative">
                <Button variant="ghost" size="icon">
                  <Bell className="w-5 h-5" />
                </Button>
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs bg-worldsview-red text-white flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-popover border shadow-lg p-0">
              {/* Notification Header */}
              <div className="p-3 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Notifications
                  </h3>
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={markAllAsRead}
                      className="text-xs h-7"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Mark all read
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Notifications List */}
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/50">
                    {notifications.slice(0, 8).map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-3 hover:bg-muted/30 cursor-pointer transition-colors ${
                          !notification.read ? 'bg-muted/10' : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon('announcement')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <h4 className={`text-sm font-medium ${
                                  !notification.read ? 'text-foreground' : 'text-muted-foreground'
                                }`}>
                                  {notification.announcement.title}
                                </h4>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {notification.announcement.content.length > 90 
                                    ? notification.announcement.content.substring(0, 90) + '...' 
                                    : notification.announcement.content}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {!notification.read && (
                                  <div className="w-2 h-2 rounded-full bg-worldsview-blue" />
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {formatTimeAgo(new Date(notification.announcement.publish_date))}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Footer */}
              {notifications.length > 8 && (
                <div className="p-3 border-t border-border/50">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => setActiveSection('announcements')}
                  >
                    View all notifications ({notifications.length})
                  </Button>
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="p-0 h-10 px-2 rounded-full flex items-center space-x-2 hover:bg-accent"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-worldsview-blue text-white font-medium">
                    {appUser?.first_name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover border shadow-lg">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{appUser?.first_name} {appUser?.last_name}</p>
                  <p className="text-xs text-muted-foreground">{appUser?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Partner Switcher - Only for Amber Hoogh */}
              {appUser?.partners && appUser?.partners?.length >= 1 && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide px-2 py-1.5">
                    Switch Partner Location
                  </DropdownMenuLabel>
                  <div className="px-2 py-1 max-h-60 overflow-y-auto">
                    {appUser.partners.map((partner, index) => {
                      const isActive = partner.id === (currentPartner.id || 0);
                      const TierIcon = getTierIcon(partner.tier);
                      return (
                        <button
                          key={index}
                          onClick={() => switchPartner(partner)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition-colors text-left ${
                            isActive 
                              ? 'bg-worldsview-purple/10 border border-worldsview-purple/30' 
                              : 'hover:bg-accent'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Building2 className={`w-4 h-4 shrink-0 ${isActive ? 'text-worldsview-purple' : 'text-muted-foreground'}`} />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm truncate ${isActive ? 'font-medium text-worldsview-purple' : ''}`}>
                                {partner.name}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge variant="outline" className="text-xs px-1.5 py-0 h-4">
                                  {toUpper(partner.company_id)}
                                </Badge>
                                <div className="flex items-center gap-1">
                                  <TierIcon className="w-3 h-3" />
                                  <span className="text-xs text-muted-foreground">
                                    {partner.tier.charAt(0).toUpperCase() + partner.tier.slice(1)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {isActive && (
                            <CheckCircle2 className="w-4 h-4 text-worldsview-purple shrink-0 ml-2" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}
              
              <DropdownMenuItem onClick={() => setActiveSection('profile')}>
                <User className="w-4 h-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={logout}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}